<template>
  <add-image  v-if="imageForActions === '' && imageResult === ''">
    <div style="text-align: center; margin-bottom: 12px; color: var(--secondary-100);">{{ $t('menu.upscale') }}</div>
    <img :src="`${baseUrl}/images/multi-menu/upscale.png`" alt="">
  </add-image>
  <div
    v-else-if="imageForActions !== '' && imageResult === ''"
    class="service__container upscale"
    :class="{ 'service__container--mobile': isMobile }"
  >
    <div class="upscale-edit">
      <image-finetune
        class="rupscale-edit__finetune"
        v-if="isEdit"
        :src="imageForActions"
        @reset="isEdit = false"
        @save="editHandler"
        :actions="['reset', 'save']"
      />
      <template v-else>
      <div class="upscale-edit__settings">
        <div class="upscale-edit__settings__title">
          {{ $t('upscale.settings-title') }}
        </div>
        <div class="upscale-edit__settings__content">
          <div class="upscale-edit__settings__sizes">
            <ai-clear-button
              v-for="(size, index) in sizesList"
              :key="index"
              class="upscale-edit__settings__sizes__item"
              :class="{ 'upscale-edit__settings__sizes__item--active': sizeIndex === index }"
              @click="setSize(index)"
            >
              <div class="upscale-edit__settings__sizes__item__icon"></div>
              <div class="upscale-edit__settings__sizes__item__text">×{{ size.scale }}</div>
              <v-spacer />
              <div class="upscale-edit__settings__sizes__item__value">
                {{ size.width }}
                <div class="upscale-edit__divider">×</div>
                {{ size.height }} px
              </div>
            </ai-clear-button>
            <ai-clear-button
              class="upscale-edit__settings__sizes__item"
              :class="{ 'upscale-edit__settings__sizes__item--active': sizeIndex === -1 }"
              @click="setSize(-1)"
            >
              <div style="position: relative; width: 100%" class="d-flex align-center">
                <div class="upscale-edit__settings__sizes__item__icon"></div>
                <div class="upscale-edit__settings__sizes__item__text">{{ $t('editor.settings.custom') }}</div>
                <v-spacer />
                <div class="upscale-edit__settings__sizes__item__value">
                  <v-text-field
                    v-model.number.lazy="customWidth"
                    @input="updateCustomHeight"
                    class="upscale-edit__input"
                    hide-details
                    height="18"
                  />
                  <div class="upscale-edit__divider">×</div>
                  <v-text-field
                    v-model.number.lazy="customHeight"
                    @input="updateCustomWidth"
                    class="upscale-edit__input"
                    hide-details
                  />
                </div>
              </div>
            </ai-clear-button>
          </div>
          <ai-button
            v-if="isMobile"
            :disabled="generationLoading"
            theme="gradient-primary"
            @click="upscaleFileHandler"
            class="upscale-edit__settings__button"
          >
            <template v-if="!generationLoading">
              {{ $t('upscale.upscale') }}
            </template>
            <time-counter v-else />
          </ai-button>
        </div>
      </div>
      <div class="upscale-edit__workarea">
        <ai-card :radius="16" class="upscale-edit__workarea__card">
          <div class="upscale-edit__workarea__container">
            <img :src="imageForActions" class="upscale-edit__workarea__image" alt="" style="height: 100%; object-fit: contain"/>
            <ai-drop-loader v-if="generationLoading" />
          </div>
          <div class="upscale-edit__workarea__data">
            <!-- <div class="upscale-edit__workarea__data__info">
              {{ imageWidth }} × {{ imageHeight }}px | {{ (imageSize / 1024).toFixed(2) }} kb
            </div> -->
            <v-spacer />
            <div class="upscale-edit__workarea__actions">
              <ai-button
                theme="dark"
                @click="
                  imageForActions = '';
                  imageResult = '';
                "
                >{{ $t('services.reset') }}</ai-button
              >
              <ai-button
                v-if="!isMobile"
                :disabled="generationLoading"
                theme="gradient-primary"
                class="upscale-edit__workarea__button"
                @click="upscaleFileHandler"
              >
                <template v-if="!generationLoading">
                  {{ $t('upscale.upscale') }}
                </template>
                <time-counter v-else />
              </ai-button>
            </div>
          </div>
        </ai-card>
      </div>
      </template>
    </div>
  </div>
  <result-image v-else-if="imageForActions !== '' && imageResult !== ''" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useEventBus } from '@vueuse/core';
import { useI18n } from 'vue-i18n';

import { useServicesStore } from '@/store/services';
import { useCommonStore } from '@/store/common';

// import { getBase64Size } from '@/helpers/getBase64Size';
import { removeBase64Prefix } from '@/helpers/removeBase64Prefix';

import AddImage from '@/components/common/AddImage/AddImage.vue';
import ImageFinetune from '@/components/common/ImageFinetune/ImageFinetune.vue';
import ResultImage from '@/components/common/ResultImage/ResultImage.vue';
import TimeCounter from '@/components/common/TimeCounter/TimeCounter.vue';

import AiButton from '@/components/UI/AiButton/AiButton.vue';
import AiCard from '@/components/UI/AiCard/AiCard.vue';
import AiClearButton from '@/components/UI/AiClearButton/AiClearButton.vue';
import AiDropLoader from '@/components/UI/AiDropLoader/AiDropLoader.vue';

const sizeLimit = 2048;

const { t } = useI18n();
const baseUrl = process.env.VUE_APP_BASE_URL;

const busError = useEventBus<string>('toast-error');

const { imageForActions, imageResult, imagePrev } = storeToRefs(useServicesStore());
const { isMobile } = storeToRefs(useCommonStore());

const { generateImage } = useServicesStore();

const generationLoading = ref<boolean>(false);
const key = ref<number>(0);
const sizeIndex = ref<number>(0);
const customWidth = ref<number>(0);
const customHeight = ref<number>(0);
const imageWidth = ref<number>(0);
const imageHeight = ref<number>(0);
const isEdit = ref<boolean>(false);

// const imageSize = computed(() => getBase64Size(imageForActions.value));

const sizesList = computed(() => {
  const biggerSize = imageWidth.value > imageHeight.value ? imageWidth.value : imageHeight.value;
  const sizesDelta = sizeLimit - biggerSize;
  const sizes = [];
  if (sizesDelta > 0) {
    sizes.push(
      {
        width: Math.round(imageWidth.value * ((biggerSize + sizesDelta * 0.33) / biggerSize)),
        height: Math.round(imageHeight.value * ((biggerSize + sizesDelta * 0.33) / biggerSize)),
        scale: ((biggerSize + sizesDelta * 0.33) / biggerSize).toFixed(1),
      },
      {
        width: Math.round(imageWidth.value * ((biggerSize + sizesDelta * 0.66) / biggerSize)),
        height: Math.round(imageHeight.value * ((biggerSize + sizesDelta * 0.66) / biggerSize)),
        scale: ((biggerSize + sizesDelta * 0.66) / biggerSize).toFixed(1),
      },
      {
        width: Math.round(imageWidth.value * (sizeLimit / biggerSize)),
        height: Math.round(imageHeight.value * (sizeLimit / biggerSize)),
        scale: (sizeLimit / biggerSize).toFixed(1),
      },
    );
  } else {
    sizes.push({
      width: Math.round(imageWidth.value * (sizeLimit / biggerSize)),
      height: Math.round(imageHeight.value * (sizeLimit / biggerSize)),
      scale: (sizeLimit / biggerSize).toFixed(1),
    });
  }
  return sizes;
});

const upscaleFileHandler = (): void => {
  generationLoading.value = true;
  const newHeight = sizeIndex.value === -1 ? customHeight.value : sizesList.value[sizeIndex.value].height;
  const newWidth = sizeIndex.value === -1 ? customWidth.value : sizesList.value[sizeIndex.value].width;

  generateImage({
    action: 'upscale',
    image: removeBase64Prefix(imageForActions.value),
    params: {
      size: {
        height: newHeight,
        width: newWidth,
      },
    },
  })
    .then((res) => {
      imageResult.value = `data:image/jpeg;base64,${res.image}`;
      imagePrev.value = imageForActions.value;
    })
    .catch((e) => {
      busError.emit(e?.error || t('common.err-gen'));
    })
    .finally(() => {
      generationLoading.value = false;
    });
};
const setSize = (index: number): void => {
  sizeIndex.value = index;
};

const updateCustomHeight = (): void => {
  customHeight.value = Math.round(customWidth.value * (imageHeight.value / imageWidth.value));
};

const updateCustomWidth = (): void => {
  customWidth.value = Math.round(customHeight.value * (imageWidth.value / imageHeight.value));
};

const imageSizeHandler = (): void => {
  if (imageForActions.value !== '') {
    const img = new Image();
    img.onload = () => {
      imageWidth.value = img.width;
      imageHeight.value = img.height;
      customWidth.value = img.width;
      customHeight.value = img.height;
    };
    img.src = imageForActions.value;
    key.value += 1;
  }
};

const editHandler = (image: string): void => {
  imageForActions.value = image;
  isEdit.value = false;
};

onMounted(() => {
  imageSizeHandler();
  setSize(0);
});

watch(imageForActions, () => {
  imageSizeHandler();
  setSize(0);
});
</script>

<style scoped lang="scss" src="./Upscale.scss" />
